// src/pages/AiResultPage.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bot, CheckCircle, Lightbulb, Copy, RefreshCw, AlertCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { copyToClipboard } from '../utils/formatters';

export default function AiResultPage({ showToast }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { request } = useApi();
  
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const { query, type } = location.state || {};

  useEffect(() => {
    let active = true;
    if (!query) {
      navigate('/dashboard');
      return;
    }
    fetchResult(active);
    return () => { active = false; };
  }, [query, type]);

  const fetchResult = async (active) => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = type === 'fix' ? '/api/ai/get-fix' : '/api/ai/diagnose';
      const body = { issue: query };
      
      const data = await request(endpoint, { method: 'POST', body: JSON.stringify(body) });
      if (!active) return;
      if (data && data.result) {
        try {
          const parsed = typeof data.result === 'string' ? JSON.parse(data.result) : data.result;
          setResult(parsed);
        } catch {
          setResult({ summary: data.result, steps: [], tips: [] });
        }
      } else {
        setError('Unable to generate response. Please try again.');
      }
    } catch (err) {
      console.error("AI Result Error:", err);
      setError('Unable to generate response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    const textToCopy = `${result.summary}\n\nSteps:\n${result.steps?.join('\n')}\n\nTips:\n${result.tips?.join('\n')}`;
    
    try {
      await copyToClipboard(textToCopy);
      showToast?.('Copied to clipboard!', 'success');
    } catch (err) {
      console.error("Copy failed", err);
      showToast?.("Failed to copy details", 'error');
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  return (
    <div style={{ height: 'calc(100vh - 124px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ maxWidth: '900px', width: '100%', margin: '0 auto', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        
        {/* Header Section (Always Visible) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, flexShrink: 0 }}>
          <button 
            onClick={() => navigate('/dashboard')}
            style={{ 
              background: 'none', 
              border: 'none', 
              padding: 0, 
              cursor: 'pointer', 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 4, 
              fontSize: 11, 
              fontWeight: 600, 
              color: 'var(--text-muted)',
              alignSelf: 'flex-start',
              transition: 'color 0.2s ease-in-out'
            }}
            onMouseOver={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
            onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
          >
            ← Back to Dashboard
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: 'var(--brand-light)', padding: 6, borderRadius: 8, color: 'var(--brand)' }}>
              <Bot size={20} />
            </div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>AI Assistant Result</h1>
          </div>
        </div>

        {loading ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <RefreshCw size={32} className="animate-spin" color="var(--text-secondary)" />
            <div style={{ fontSize: 15, color: 'var(--text-secondary)', fontWeight: 500 }}>Generating your solution...</div>
          </div>
        ) : error ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ padding: '40px', textAlign: 'center', background: '#FEF2F2', borderRadius: '16px', border: '1px solid #FEE2E2', maxWidth: 400 }}>
              <AlertCircle size={40} color="#EF4444" style={{ marginBottom: 16 }} />
              <div style={{ fontSize: 16, color: '#991B1B', fontWeight: 600 }}>{error}</div>
              <button 
                onClick={fetchResult}
                className="btn btn-primary"
                style={{ marginTop: 20 }}
              >
                Retry Generation
              </button>
            </div>
          </div>
        ) : (
          <motion.div 
            variants={fadeUp} initial="hidden" animate="visible"
            style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--surface)', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-card)' }}
          >
            {/* User Request Section (Fixed) */}
            <div style={{ padding: '16px 24px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Your Request</div>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', fontWeight: 500, lineHeight: 1.4 }}>"{query}"</div>
            </div>

            {/* AI Summary Section (Fixed) */}
            <div style={{ padding: '24px 24px 16px', flexShrink: 0, borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Summary</div>
              <div style={{ fontSize: 20, color: 'var(--text-primary)', fontWeight: 700, lineHeight: 1.5, letterSpacing: '-0.01em' }}>
                {result.summary}
              </div>
            </div>

            {/* Scrollable Results Section */}
            <div className="steps-container" style={{ flex: 1, overflowY: 'auto', padding: '16px 24px 24px' }}>
              <style>{`
                .steps-container::-webkit-scrollbar { width: 6px; }
                .steps-container::-webkit-scrollbar-thumb { background: var(--text-e2e8f0); border-radius: 10px; }
              `}</style>
              
              {result.steps?.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircle size={14} color="var(--text-3b82f6)" /> Recommended Fix Steps
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {result.steps.map((step, i) => (
                      <div 
                        key={i} 
                        style={{ 
                          display: 'flex', 
                          gap: 16, 
                          alignItems: 'flex-start', 
                          padding: '16px 20px', 
                          background: 'var(--bg)', 
                          borderRadius: '12px', 
                          border: '1px solid var(--border)',
                          transition: 'all 0.2s ease-in-out'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.4)'; e.currentTarget.style.boxShadow = '0 0 12px rgba(96, 165, 250, 0.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        <div style={{ 
                          width: 22, 
                          height: 22, 
                          background: 'var(--text-3b82f6)', 
                          color: '#FFFFFF', 
                          borderRadius: '50%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          fontSize: 11, 
                          fontWeight: 800, 
                          flexShrink: 0,
                          marginTop: 2
                        }}>
                          {i + 1}
                        </div>
                        <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500, lineHeight: 1.5 }}>{step}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.tips?.length > 0 && (
                <div style={{ 
                  padding: '20px', 
                  background: 'rgba(245, 158, 11, 0.05)', 
                  borderRadius: '12px', 
                  border: '1px solid rgba(245, 158, 11, 0.25)',
                  boxShadow: '0 0 16px rgba(245, 158, 11, 0.05)'
                }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-fbbf24)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Lightbulb size={16} color="var(--text-fbbf24)" /> Expert Insights
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {result.tips.map((tip, i) => (
                      <div key={i} style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500, display: 'flex', gap: 8, lineHeight: 1.4 }}>
                        <span style={{ color: 'var(--text-fbbf24)' }}>•</span>
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Actions Row (Fixed) */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0, display: 'flex', gap: 12 }}>
              <button 
                onClick={handleCopy}
                style={{ 
                  flex: 1, 
                  background: 'var(--brand-dark)', 
                  color: '#FFFFFF', 
                  border: 'none', 
                  padding: '12px', 
                  borderRadius: '10px', 
                  fontWeight: 700, 
                  cursor: 'pointer', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: 8, 
                  fontSize: 14, 
                  transition: 'all 0.2s ease' 
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--text-primary)'; e.currentTarget.style.color = 'var(--surface)'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'var(--brand-dark)'; e.currentTarget.style.color = '#FFFFFF'; }}
              >
                <Copy size={18} /> Copy Solution
              </button>
              <button 
                onClick={() => navigate('/dashboard')}
                style={{ 
                  background: 'var(--white-10)', 
                  color: 'var(--text-primary)', 
                  border: '1px solid var(--white-15)', 
                  padding: '12px 24px', 
                  borderRadius: '10px', 
                  fontWeight: 700, 
                  cursor: 'pointer', 
                  fontSize: 14, 
                  transition: 'all 0.2s ease',
                  boxShadow: 'var(--shadow)'
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--white-15)'; e.currentTarget.style.borderColor = 'var(--white-20)'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'var(--white-10)'; e.currentTarget.style.borderColor = 'var(--white-15)'; }}
              >
                Done
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
